import {
  Box,
  Typography,
  useTheme,
  FormControl,
  Select,
  MenuItem,
  Tooltip,
} from '@mui/material';
import Chip from '@mui/material/Chip';
import Stack from '@mui/material/Stack';
import {
  type GridSortModel,
  type GridColumns,
  type GridValidRowModel,
} from '@mui/x-data-grid';
import { CustomDataGrid } from 'components';
import { YesNoModal } from 'components/shared';
import {
  activeStatusOptions,
  commonConstantTexts,
  exportExcelFileNames,
} from 'constants/constantText';
import { strings } from 'i18n/Strings';
import { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import {
  fetchExportExcelData,
  getAllCustomers,
  getCustomersTotalPot,
  updateCustomerActiveStatusService,
} from 'service/CustomerService';
import {
  setCurrentPage,
  setSortBy,
  setSortOrder,
} from 'store/component/pagination';
import { setSnackBarFailed, setSnackBarSuccess } from 'store/snackBar';
import { colorTheme } from 'theme';
import { checkSortingValues } from 'utils/GeneralUtil';

interface ICustomerData {
  id: string;
  firstName: string;
  lastName: string;
  calling_code: string;
  phone: string;
  email: string;
  rows: number;
  count: number;
}
interface ICustomerResponse {
  basicInfo: {
    count: number;
    rows: ICustomerData[];
  };
}

export const sortOptions = [
  { id: 1, value: 'Date of account creation – Newest to oldest' },
  { id: 2, value: 'Date of account creation – Oldest to newest' },
  // Will be used in future
  // { id: 3, value: 'MM account value – High to low' },
  // { id: 4, value: 'MM account value – Low to high' },
];

type GridRowData = Record<string, unknown>;

const Customers = (): JSX.Element => {
  const navigate = useNavigate();
  const theme = useTheme();
  const colors = colorTheme(theme.palette.mode);
  const [customerData, setCustomerData] = useState<GridRowData[]>([]);
  const [filters, setFilter] = useState<string[]>([]);
  const [openConfirmation, setOpenConfirmation] = useState(false);
  const [confirmationText, setConfirmationText] = useState('');
  const [loader, setLoader] = useState(false);

  const [filtersToDisplay, setFiltersToDisplay] = useState<string[]>([]);
  const [openSortDropdown, setOpenSortDropDown] = useState(false);
  const [openFilterDropdown, setOpenFilterDropdown] = useState(false);
  const [pageCount, setPageCount] = useState(null);
  // const [sortBorderColor, setSortBorderColor] = useState('');
  const filterRef = useRef<HTMLElement>(null);
  const sortRef = useRef<HTMLElement>(null);
  const selectedRecord = useRef<string>(null); // Initial value

  const pageInfo = useSelector((state) => state?.pagination?.customers);
  const [searchValue, setSearchValue] = useState('');
  const dispatch = useDispatch();

  const closeFilterDropdown = useCallback(
    (e: MouseEvent) => {
      if (
        filterRef.current &&
        openFilterDropdown &&
        !filterRef.current.contains(e.target as Node)
      ) {
        setOpenFilterDropdown(false);
      }
    },
    [openFilterDropdown],
  );

  const closeSortDropdown = useCallback(
    (e: MouseEvent) => {
      if (
        sortRef.current &&
        openSortDropdown &&
        !sortRef.current.contains(e.target as Node)
      ) {
        setOpenSortDropDown(false);
      }
    },
    [openSortDropdown],
  );
  // ExportExcel
  const downloadExcelFile = async (): Promise<void> => {
    try {
      const url = await fetchExportExcelData();
      const link = document.createElement('a');
      link.href = url as string;
      link.download = exportExcelFileNames.CUSTOMER_EXPORT;
      link.click();
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.customerDataExportSuccess,
        }),
      );
    } catch (error) {
      console.log('Error', error);
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: strings.displayText.customerDataExportError,
        }),
      );
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', closeFilterDropdown);
  }, [closeFilterDropdown]);

  useEffect(() => {
    document.addEventListener('mousedown', closeSortDropdown);
  }, [closeSortDropdown]);

  const handleSelectStatus = (event, row): void => {
    const { value } = event.target;
    console.log(event.target.value, row.id);
    selectedRecord.current = { row, value };
    setOpenConfirmation(true);
    setConfirmationText(
      strings.displayText.updateActiveStatus.replace(`{status}`, value),
    );
  };

  const columns: GridColumns<GridValidRowModel> = [
    {
      field: 'id',
      flex: 0.8,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.employeeID}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.id}
        </Typography>
      ),
    },
    {
      field: 'uniqueId',
      flex: 1,
      cellClassName: 'name-column--cell',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.customersId}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.uniqueId}
        </Typography>
      ),
    },
    {
      field: 'first_name',
      flex: 0.8,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.name}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.first_name} {row.last_name}
        </Typography>
      ),
    },
    {
      field: 'email',
      flex: 1.2,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.emailAddress}
        </Typography>
      ),
      renderCell: ({ row }) => {
        const maxLength = 25; // Adjust as needed
        const email: string = row.email;
        const truncatedEmail =
          email.length > maxLength ? email.slice(0, maxLength) + '...' : email;

        return (
          <Tooltip title={email} placement="top">
            <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
              {truncatedEmail}
            </Typography>
          </Tooltip>
        );
      },
    },
    {
      field: 'calling_code/phone',
      sortable: false,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.phoneNumber}
        </Typography>
      ),
      flex: 0.9,
      renderCell: ({ row }) => {
        const formatPhoneNumber = (
          callingCode: string,
          phone: string,
        ): string => {
          if (!callingCode || !phone) return '';
          const prefix = phone.slice(0, 2);
          const rest = phone.slice(2);
          const sign = callingCode.startsWith('+') ? '' : '+';
          return `${sign}${callingCode} ${prefix} ${rest}`;
        };
        const formattedPhone = formatPhoneNumber(row.calling_code, row.phone);
        return (
          <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
            {formattedPhone}
          </Typography>
        );
      },
    },
    {
      field: 'registrationStatus',
      flex: 0.8,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.registration}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.registrationStatus}
        </Typography>
      ),
    },
    {
      field: 'createdByAcorn',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.createdByAcorn}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography
          variant="h5"
          color={colors.gray[100]}
          fontSize={14}
          sx={{ width: '100%', textAlign: 'center' }}
        >
          {row.createdByAcorn ? 'Yes' : 'No'}
        </Typography>
      ),
    },
    {
      field: 'active_status',
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.status}
        </Typography>
      ),
      flex: 1,
      renderCell: ({ row }) => {
        return (
          <FormControl fullWidth>
            <Select
              disabled={row.activeStatus === commonConstantTexts.close}
              margin="normal"
              label="Status"
              value={row.activeStatus}
              onChange={(event: unknown) => {
                handleSelectStatus(event, row);
              }}
            >
              {activeStatusOptions.map((option, i) => (
                <MenuItem
                  key={i}
                  value={option.value}
                  // Prevent accidental unblocking when sanctions screening has hit
                  disabled={
                    option.value === commonConstantTexts.unblock &&
                    row.watchlistSanctionsHit === true
                  }
                >
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        );
      },
    },
    {
      field: 'companyName',
      flex: 1,
      renderHeader: () => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {strings.displayText.companyName}
        </Typography>
      ),
      renderCell: ({ row }) => (
        <Typography variant="h5" color={colors.gray[100]} fontSize={14}>
          {row.companyName || '-'}
        </Typography>
      ),
    },
  ];

  const getCustomerData = async (): Promise<void> => {
    try {
      const res = await getAllCustomers({
        page: Number(pageInfo?.page) + 1,
        size: pageInfo?.pageSize,
        sortBy: pageInfo?.sortBy,
        sortOrder: pageInfo?.sortOrder,
        searchValue,
      });
      const customerInfo = res as unknown as ICustomerResponse;
      setCustomerData(customerInfo?.basicInfo?.rows);
      setPageCount(customerInfo?.basicInfo?.count);
    } catch (error) {}
  };

  useEffect(() => {
    void getCustomerData();
  }, [
    pageInfo?.page,
    pageInfo.pageSize,
    pageInfo?.sortBy,
    pageInfo?.sortOrder,
    searchValue,
  ]);
  // Will be used in future

  // const handleFilterAddition = (event: SelectChangeEvent<string[]>): void => {
  //   const {
  //     target: { value },
  //   } = event;
  //   setFilter(
  //     // On autofill we get a stringified value.
  //     typeof value === 'string' ? value.split(',') : value,
  //   );
  // };
  const handleDeleteFilter = (i: number): void => {
    setFiltersToDisplay(
      filtersToDisplay.filter((filterOption, index) => index !== i),
    );
    setFilter(filters.filter((filter, index) => index !== i));
  };

  const handleSearch = async (
    event: React.ChangeEvent<HTMLInputElement>,
  ): Promise<void> => {
    setSearchValue(event.target.value);
    dispatch(setCurrentPage({ key: 'customers', value: 0 }));
  };

  const handleSortChange = (model: GridSortModel): void => {
    dispatch(
      setSortBy({
        key: 'customers',
        value: model[0].field,
      }),
    );
    dispatch(setSortOrder({ key: 'customers', value: model[0].sort }));
    dispatch(setCurrentPage({ key: 'customers', value: 0 }));
  };

  const handleUpdateActiveStatus = async (): void => {
    setLoader(true);

    const { value, row } = selectedRecord.current;
    try {
      // Only check totalPot when closing customer, not when blocking
      if (value === 'close') {
        const customerTotalPot = await getCustomersTotalPot(row.id);

        const zenpensionsTotal = Number(
          customerTotalPot?.unitDetails?.totatUnitPrice ?? 0,
        );

        const otherPensionsTotal = Number(
          customerTotalPot?.otherPensionsTotal ?? 0,
        );

        const totalPot = zenpensionsTotal + otherPensionsTotal;
        if (totalPot !== 0) {
          const errorMessage =
            'Unable to close: The total pot balance must be zero before closing the account.';
          dispatch(
            setSnackBarFailed({
              snackBarMessage: errorMessage,
            }),
          );
          setLoader(false);
          setOpenConfirmation(false);
          setConfirmationText('');
          selectedRecord.current = null;
          return;
        }
      }

      const response = await updateCustomerActiveStatusService(row.id, value);
      void getCustomerData();
      selectedRecord.current = null;
      setOpenConfirmation(false);
      setConfirmationText('');
      setLoader(false);
      dispatch(
        setSnackBarSuccess({
          snackBarMessage: response.message,
        }),
      );
    } catch (err: unknown) {
      setLoader(false);
      if (err instanceof Error) {
        const errorMessage =
          'Unable to update: An error occurred while updating the status.';
        dispatch(
          setSnackBarFailed({
            snackBarMessage: errorMessage,
          }),
        );
      } else {
        dispatch(
          setSnackBarFailed({
            snackBarMessage: 'An unknown error occurred.',
          }),
        );
      }
    }
  };

  const styles = {
    customersMain: {
      display: 'flex',
      justifyContent: 'space-between',
      height: 50,
      marginBottom: 2,
    },
    customerHeadGrid: {
      display: 'flex',
      justifyContent: 'space-evenly',
    },
    customersHeaderBox: {
      marginTop: 1,
    },
    filterBtnBox: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    selectBox: { display: 'flex', marginRight: '20px' },
    filterSelect: {
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#BFBFBF',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#BFBFBF',
      },
      '& .MuiSelect-icon': {
        color: '#fff',
      },

      color: '#fff',
      minHeight: 50,
      minWidth: 150,
      backgroundColor: '#1c3e41',
      borderRadius: '7px',
      borderWidth: '2px',
      borderColor: filters.length > 0 ? colors.primaryGreen[300] : '#000',
    },
    filterByBox: {
      display: 'flex',
      gap: 1,
    },
    filterByTypo: {
      fontSize: '16px',
    },
    applyCancelBox: {
      display: 'flex',
      justifyContent: 'space-between',
      marginRight: 2,
      marginLeft: 2,
    },
    applyBtn: {
      color: colors.greenAccent[500],
      fontWeight: '600',
      padding: 1,
    },
    cancelBtn: {
      color: colors.gray[500],
      fontWeight: '600',
      padding: 1,
    },
    sortMainBox: {
      minWidth: 120,
    },
    select: {
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#BFBFBF',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#BFBFBF',
      },
      '& .MuiSelect-icon': {
        color: '#fff',
      },
      color: '#fff',
      maxHeight: 50,
      backgroundColor: '#1c3e41',
      borderRadius: '7px',
      // borderColor: sortBorderColor,
    },
    chipBox: {
      backgroundColor: '#B5DFC8',
      color: '#000000',
      mb: 1,
    },
    excelBtn: {
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: '#BFBFBF',
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: '#BFBFBF',
      },
      '& .MuiSelect-icon': {
        color: '#fff',
      },
      color: '#fff',
      minHeight: 50,
      minWidth: 120,
      backgroundColor: '#1c3e41',
      borderRadius: '7px',
      borderWidth: '2px',
      mx: 2,
    },
    selectRenderBox: {
      display: 'flex',
      gap: 1,
    },
    stackBox: {
      display: 'flex',
      justifyContent: 'flex-end',
      mb: 2,
    },
    stackBtn: {
      flexFlow: 'row-reverse wrap',
    },
  };
  return (
    <Fragment>
      <Box sx={styles.stackBox}>
        <Stack direction="row" spacing={1} sx={styles.stackBtn}>
          {filtersToDisplay.map((filterOption, i) => (
            <Chip
              key={i}
              label={filterOption}
              variant="filled"
              sx={styles.chipBox}
              onDelete={() => {
                handleDeleteFilter(i);
              }}
            />
          ))}
        </Stack>
      </Box>
      <Box sx={{ cursor: 'pointer' }}>
        <CustomDataGrid
          title="Customers"
          columns={columns}
          rows={customerData}
          isShowExport={true}
          onExportClick={() => {
            void downloadExcelFile();
          }}
          pageCount={pageCount}
          searchEnabled={true}
          handleSearch={handleSearch}
          getRowId={(row: GridRowData) => `${String(row.id)}`}
          getRowClassName={(params) =>
            params.indexRelativeToCurrentPage % 2 === 0 ? 'even' : 'odd'
          }
          onRowClick={(row) => {
            navigate(`/customer-details/${row.id}`);
          }}
          currentPage="customers"
          sortModel={checkSortingValues(pageInfo)}
          sortingMode={strings.displayText.sortingServer}
          onSortModelChange={(mode) => {
            handleSortChange(mode);
          }}
        />
      </Box>
      {/* <AlertModal
        open={openConfirmation}
        onClose={() => {
          setOpenConfirmation(false);
        }}
        handleConfirm={() => {
          setTimeout(() => {}, 3000);
        }}
        title={confirmationText}
        isButtonForConfirmation={true}
      /> */}

      <YesNoModal
        open={openConfirmation}
        onClose={() => {
          setOpenConfirmation(false);
          selectedRecord.current = null;
          setOpenConfirmation(false);
          setConfirmationText('');
          setLoader(false);
        }}
        handleConfirm={handleUpdateActiveStatus}
        title={confirmationText}
        isDisabled={loader}
      />
    </Fragment>
  );
};

export default Customers;
